import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  hasCoachBillingPaidAccess,
  hasValidOptionalBillingTimestamps,
} from "@/lib/soloLeaderBilling";

import type { StripeTier } from "@/lib/stripeTiers";

export type SubscriptionStatus = "free" | "trialing" | "active" | "past_due" | "canceled" | "unpaid";

export interface SubscriptionData {
  id: string;
  status: SubscriptionStatus;
  planType?: string | null;
  leaderQuantity: number;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  promoCodeUsed: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  paymentFailureGraceEndsAt?: string | null;
}

export interface UseSubscriptionReturn {
  subscription: SubscriptionData | null;
  plan: StripeTier;
  status: SubscriptionStatus;
  leaderLimit: number | null;
  isTrialing: boolean;
  trialEndsAt: Date | null;
  isPastDue: boolean;
  isActive: boolean;
  isCanceled: boolean;
  isLoading: boolean;
}

interface SubscriptionRow {
  id: string;
  status: string;
  plan_type?: string | null;
  leader_quantity: number;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  trial_end: string | null;
  promo_code_used: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  payment_failure_grace_ends_at?: string | null;
}

export function useSubscription(): UseSubscriptionReturn {
  const { user } = useAuth();

  const { data: subscription, isLoading } = useQuery({
    queryKey: ["subscription", user?.id],
    queryFn: async (): Promise<SubscriptionData | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Failed to fetch subscription:", error);
        return null;
      }

      if (!data) {
        // No subscription row yet — create a free one
        const { data: newSub, error: insertError } = await supabase
          .from("subscriptions")
          .insert(
            { user_id: user.id, status: "free", leader_quantity: 1 },
            { onConflict: "user_id", ignoreDuplicates: true },
          )
          .select()
          .maybeSingle();

        if (insertError) {
          console.error("Failed to create subscription:", insertError);
          const isDuplicate =
            insertError.code === "23505" ||
            insertError.message?.toLowerCase().includes("duplicate");
          if (!isDuplicate) {
            return null;
          }
        }

        if (newSub) {
          return mapRow(newSub);
        }

        // Another concurrent client may have inserted the row first.
        const { data: existingSub, error: existingError } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();
        if (existingError) {
          console.error("Failed to fetch subscription after create race:", existingError);
          return null;
        }
        return existingSub ? mapRow(existingSub) : null;
      }

      return mapRow(data);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const status: SubscriptionStatus = subscription?.status ?? "free";
  const billingTimestampsValid = hasValidOptionalBillingTimestamps({
    currentPeriodEnd: subscription?.currentPeriodEnd,
    paymentFailureGraceEndsAt: subscription?.paymentFailureGraceEndsAt,
  });
  const isPaid = billingTimestampsValid &&
    hasCoachBillingPaidAccess({
      status,
      currentPeriodEnd: subscription?.currentPeriodEnd,
      cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd,
      paymentFailureGraceEndsAt: subscription?.paymentFailureGraceEndsAt,
    });

  const plan: StripeTier = isPaid ? "growth" : "free";
  const leaderLimit = isPaid ? null : 1;

  return {
    subscription: subscription ?? null,
    plan,
    status,
    leaderLimit,
    isTrialing: status === "trialing",
    trialEndsAt: subscription?.trialEnd ? new Date(subscription.trialEnd) : null,
    isPastDue: status === "past_due",
    isActive: status === "active" || status === "trialing",
    isCanceled: status === "canceled",
    isLoading,
  };
}

function mapRow(row: SubscriptionRow): SubscriptionData {
  return {
    id: row.id,
    status: row.status as SubscriptionStatus,
    planType: row.plan_type ?? null,
    leaderQuantity: row.leader_quantity,
    cancelAtPeriodEnd: row.cancel_at_period_end,
    currentPeriodEnd: row.current_period_end,
    trialEnd: row.trial_end,
    promoCodeUsed: row.promo_code_used,
    stripeCustomerId: row.stripe_customer_id,
    stripeSubscriptionId: row.stripe_subscription_id,
    paymentFailureGraceEndsAt: row.payment_failure_grace_ends_at ?? null,
  };
}
